import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { EDUCATION_CAR_PCT } from './tripPurpose';

// Vehicle trips assigned to census zones (Employment + Other) vs schools
// (Education), from the assigned routes. Education routes carry a "SCH…"
// destination code. Returns { census, school, total }.
function assignedByPurpose(assignedNetwork) {
  const feats = (assignedNetwork && assignedNetwork.routes && assignedNetwork.routes.features) || [];
  const isSchool = d => typeof d === 'string' && d.startsWith('SCH');
  const census = feats.filter(f => !isSchool(f.properties.destination)).reduce((a, f) => a + (f.properties.trips || 0), 0);
  const school = feats.filter(f => isSchool(f.properties.destination)).reduce((a, f) => a + (f.properties.trips || 0), 0);
  return { census, school, total: census + school };
}

function formatDate() {
  return new Date().toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
}

function sectionHeader(doc, y, pageW, text) {
  doc.setFillColor(245, 240, 250);
  doc.rect(14, y, pageW - 28, 8, 'F');
  doc.setTextColor(61, 26, 110);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(text, 17, y + 5.5);
  return y + 12;
}

function checkPage(doc, y, needed = 40) {
  if (y + needed > 275) { doc.addPage(); return 20; }
  return y;
}

// Render one figure image on its own full landscape A4 page, scaled to fill the
// page while preserving aspect ratio (source figures are 800×500). Caller is
// responsible for returning to portrait afterwards if more portrait content follows.
function addLandscapeFigure(doc, title, imgDataUrl, caption) {
  doc.addPage('a4', 'landscape');
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const y = sectionHeader(doc, 14, w, title);
  const captionH = caption ? 8 : 0;
  const maxW = w - 28;
  const maxH = h - y - 16 - captionH;         // leave room for the footer band + caption
  // Use the image's real pixel dimensions so it's fitted, never stretched.
  const props = doc.getImageProperties(imgDataUrl);
  const aspect = (props && props.width) ? props.height / props.width : 500 / 800;
  let imgW = maxW, imgH = imgW * aspect;
  if (imgH > maxH) { imgH = maxH; imgW = imgH / aspect; }
  const x = (w - imgW) / 2;
  doc.addImage(imgDataUrl, 'PNG', x, y, imgW, imgH);
  if (caption) {
    doc.setFontSize(7); doc.setTextColor(150, 150, 150); doc.setFont('helvetica', 'italic');
    doc.text(caption, 14, y + imgH + 5);
    doc.setFont('helvetica', 'normal');
  }
}

function getBounds(destLayer) {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  destLayer.features.forEach(feature => {
    const coords = feature.geometry.type === 'Polygon'
      ? feature.geometry.coordinates.flat(1)
      : feature.geometry.coordinates.flat(2);
    coords.forEach(([lng, lat]) => {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    });
  });
  return { minLng, minLat, maxLng, maxLat };
}

function lngLatToXY(lng, lat, bounds, width, height) {
  const padLng = (bounds.maxLng - bounds.minLng) * 0.08;
  const padLat = (bounds.maxLat - bounds.minLat) * 0.08;
  const x = ((lng - (bounds.minLng - padLng)) / ((bounds.maxLng + padLng) - (bounds.minLng - padLng))) * width;
  // Flip y since canvas y goes down
  const y = (1 - (lat - (bounds.minLat - padLat)) / ((bounds.maxLat + padLat) - (bounds.minLat - padLat))) * height;
  return [x, y];
}

function percentageToColourRgb(pct, maxPct) {
  const intensity = Math.min(pct / maxPct, 1);
  const r = Math.round(61 + (255 - 61) * (1 - intensity));
  const g = Math.round(26 + (255 - 26) * (1 - intensity));
  const b = Math.round(110 + (255 - 110) * (1 - intensity));
  return `rgba(${r},${g},${b},0.75)`;
}

async function renderMapToDataUrl(destLayer, mapboxToken, width = 800, height = 500, highlightCodes = null) {
  if (!destLayer || !destLayer.features || destLayer.features.length === 0) return null;

  const bounds = getBounds(destLayer);
  const padLng = (bounds.maxLng - bounds.minLng) * 0.08;
  const padLat = (bounds.maxLat - bounds.minLat) * 0.08;
  // Size the image to the study area's true proportions (longitude degrees shrink
  // by cos(lat)) so the map isn't stretched, and request it as large as Mapbox's
  // 1280px static-image cap allows for sharpness.
  const midLat = (bounds.minLat + bounds.maxLat) / 2;
  const geoAspect = ((bounds.maxLng - bounds.minLng + 2 * padLng) * Math.cos(midLat * Math.PI / 180))
    / ((bounds.maxLat - bounds.minLat + 2 * padLat) || 1);
  if (isFinite(geoAspect) && geoAspect > 0) {
    const CAP = 1280, MIN = 240;
    if (geoAspect >= 1) { width = CAP; height = Math.min(CAP, Math.max(MIN, Math.round(CAP / geoAspect))); }
    else { height = CAP; width = Math.min(CAP, Math.max(MIN, Math.round(CAP * geoAspect))); }
  }
  const bbox = `[${bounds.minLng - padLng},${bounds.minLat - padLat},${bounds.maxLng + padLng},${bounds.maxLat + padLat}]`;
  const baseUrl = `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/${bbox}/${width}x${height}?access_token=${mapboxToken}`;

  // Fetch base map tile
  let baseImageDataUrl = null;
  try {
    const response = await fetch(baseUrl);
    const blob = await response.blob();
    baseImageDataUrl = await new Promise(resolve => {
      const reader = new FileReader(); reader.onload = () => resolve(reader.result); reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.error('Base map fetch error:', err);
  }

  // Draw base map + zones onto canvas
  return new Promise(resolve => {
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#F5F0FA';
    ctx.fillRect(0, 0, width, height);

    const maxPct = Math.max(...destLayer.features.map(f => f.properties.percentage || 0), 1);

    function drawZones() {
      destLayer.features.forEach(feature => {
        const pct = feature.properties.percentage || 0;
        const isLsoa = feature.properties.type === 'lsoa';
        // In highlight mode, assigned zones are filled teal and the rest faint —
        // for the "assigned destination zones" plan.
        const hl = highlightCodes ? highlightCodes.has(feature.properties.code) : null;
        const rings = feature.geometry.type === 'Polygon'
          ? feature.geometry.coordinates
          : feature.geometry.coordinates.flat(1);

        rings.forEach(ring => {
          ctx.beginPath();
          ring.forEach(([lng, lat], i) => {
            const [x, y] = lngLatToXY(lng, lat, bounds, width, height);
            if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.fillStyle = highlightCodes
            ? (hl ? 'rgba(13,148,136,0.75)' : 'rgba(190,190,200,0.28)')
            : percentageToColourRgb(pct, maxPct);
          ctx.fill();
          ctx.strokeStyle = highlightCodes ? (hl ? '#0F6E56' : '#C4BDD4') : '#3D1A6E';
          ctx.lineWidth = isLsoa ? 0.8 : 1.2;
          ctx.stroke();
        });
      });
      resolve(canvas.toDataURL('image/png'));
    }

    if (baseImageDataUrl) {
      const img = new Image();
      img.onload = () => { ctx.drawImage(img, 0, 0, width, height); drawZones(); };
      img.onerror = () => drawZones();
      img.src = baseImageDataUrl;
    } else {
      drawZones();
    }
  });
}

function renderSchematicToDataUrl(nodes, links, siteBox, width = 800, height = 500) {
  return new Promise((resolve) => {
    if ((!nodes || nodes.length === 0) && (!links || links.length === 0)) { resolve(null); return; }

    const allX = nodes.map(n => n.x);
    const allY = nodes.map(n => n.y);
    if (siteBox) { allX.push(siteBox.x, siteBox.x + siteBox.w); allY.push(siteBox.y, siteBox.y + siteBox.h); }
    if (allX.length === 0) { resolve(null); return; }

    const minX = Math.min(...allX) - 60;
    const minY = Math.min(...allY) - 60;
    const maxX = Math.max(...allX) + 60;
    const maxY = Math.max(...allY) + 60;
    const vbW = maxX - minX || 400;
    const vbH = maxY - minY || 300;
    // Size the raster to the schematic's own proportions (not a fixed 800×500 box)
    // so it isn't stretched, at a high long-edge resolution for a sharp figure.
    const aspect = vbW / vbH;
    const LONG = 1400;
    if (isFinite(aspect) && aspect > 0) {
      if (aspect >= 1) { width = LONG; height = Math.round(LONG / aspect); }
      else { height = LONG; width = Math.round(LONG * aspect); }
    }

    const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
    if (siteBox) nodeMap['__sitebox__'] = { id: '__sitebox__', x: siteBox.x + siteBox.w / 2, y: siteBox.y + siteBox.h / 2 };

    const allTotals = links.map(l => (l.arrivals || 0) + (l.departures || 0));
    const maxTotal = Math.max(...allTotals, 1);

    function tripColor(arr, dep) {
      const total = (arr || 0) + (dep || 0);
      if (!total) return '#C4BDD4';
      const intensity = total / maxTotal;
      if (intensity > 0.66) return '#D00000';
      if (intensity > 0.33) return '#E85D00';
      return '#C89000';
    }
    function tripWidth(arr, dep) {
      const total = (arr || 0) + (dep || 0);
      if (!total) return 2;
      return 2 + (total / maxTotal) * 5;
    }

    let svgParts = [];
    svgParts.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${minX} ${minY} ${vbW} ${vbH}" width="${width}" height="${height}">`);
    svgParts.push(`<rect x="${minX}" y="${minY}" width="${vbW}" height="${vbH}" fill="white"/>`);

    // Grid
    for (let gx = Math.floor(minX / 60) * 60; gx < maxX; gx += 60) {
      for (let gy = Math.floor(minY / 60) * 60; gy < maxY; gy += 60) {
        svgParts.push(`<circle cx="${gx}" cy="${gy}" r="1" fill="#EDE5F8" opacity="0.6"/>`);
      }
    }

    // Site box
    if (siteBox) {
      svgParts.push(`<rect x="${siteBox.x}" y="${siteBox.y}" width="${siteBox.w}" height="${siteBox.h}" fill="rgba(232,0,28,0.06)" stroke="#E8001C" stroke-width="2" stroke-dasharray="8,4" rx="4"/>`);
      svgParts.push(`<text x="${siteBox.x + 6}" y="${siteBox.y + 14}" font-size="9" font-weight="bold" fill="#E8001C" font-family="Arial">SITE BOUNDARY</text>`);
    }

    // Links
    links.forEach(link => {
      const src = nodeMap[link.source];
      const tgt = nodeMap[link.target];
      if (!src || !tgt) return;
      const color = tripColor(link.arrivals, link.departures);
      const sw = tripWidth(link.arrivals, link.departures);
      const midX = tgt.x, midY = src.y;
      const pathD = `M ${src.x} ${src.y} L ${midX} ${midY} L ${tgt.x} ${tgt.y}`;

      // Arrow
      const ddx = tgt.x - midX, ddy = tgt.y - midY;
      const len = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
      const ux = ddx / len, uy = ddy / len;
      const as = 7;
      const arrowX = tgt.x - ux * 18, arrowY = tgt.y - uy * 18;
      const arrowPts = `${tgt.x},${tgt.y} ${arrowX - uy * as},${arrowY + ux * as} ${arrowX + uy * as},${arrowY - ux * as}`;

      svgParts.push(`<path d="${pathD}" stroke="${color}" stroke-width="${sw}" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`);
      svgParts.push(`<polygon points="${arrowPts}" fill="${color}"/>`);

      // Flow boxes
      const hLen = Math.abs(tgt.x - src.x), vLen = Math.abs(tgt.y - src.y);
      const useHoriz = hLen >= vLen;
      let mx, my, fux, fuy;
      if (useHoriz) { mx = (src.x + tgt.x) / 2; my = src.y; fux = tgt.x > src.x ? 1 : -1; fuy = 0; }
      else { mx = tgt.x; my = (src.y + tgt.y) / 2; fux = 0; fuy = tgt.y > src.y ? 1 : -1; }
      const px = -fuy, py = fux;
      const OFFSET = 26, BW = 52, BH = 22;
      const arrVal = link.invertFlows ? link.departures : link.arrivals;
      const depVal = link.invertFlows ? link.arrivals : link.departures;
      const hasArr = arrVal !== null && arrVal > 0;
      const hasDep = depVal !== null && depVal > 0;

      const ax2 = mx + px * OFFSET, ay2 = my + py * OFFSET;
      svgParts.push(`<rect x="${ax2 - BW/2}" y="${ay2 - BH/2}" width="${BW}" height="${BH}" rx="4" fill="${hasArr ? '#EDE5F8' : '#F9F7FD'}" stroke="${hasArr ? '#7B4DBF' : '#C4BDD4'}" stroke-width="${hasArr ? 1.5 : 1}"/>`);
      svgParts.push(`<text x="${ax2}" y="${ay2 + 4}" text-anchor="middle" font-size="10" font-weight="bold" fill="${hasArr ? '#3D1A6E' : '#C4BDD4'}" font-family="Arial">${hasArr ? arrVal : '-'}</text>`);

      const dx2 = mx - px * OFFSET, dy2 = my - py * OFFSET;
      svgParts.push(`<rect x="${dx2 - BW/2}" y="${dy2 - BH/2}" width="${BW}" height="${BH}" rx="4" fill="${hasDep ? '#F0FDF9' : '#F9F7FD'}" stroke="${hasDep ? '#0D9488' : '#C4BDD4'}" stroke-width="${hasDep ? 1.5 : 1}"/>`);
      svgParts.push(`<text x="${dx2}" y="${dy2 + 4}" text-anchor="middle" font-size="10" font-weight="bold" fill="${hasDep ? '#0D9488' : '#C4BDD4'}" font-family="Arial">${hasDep ? depVal : '-'}</text>`);

      if (link.roadName) {
        svgParts.push(`<text x="${mx}" y="${my - OFFSET - 16}" text-anchor="middle" font-size="9" fill="#6B6880" font-family="Arial">${link.roadName.toUpperCase()}</text>`);
      }
    });

    // Nodes
    const NR = 14;
    nodes.forEach(node => {
      if (node.isSite) {
        svgParts.push(`<rect x="${node.x - 26}" y="${node.y - 18}" width="52" height="36" rx="4" fill="#3D1A6E"/>`);
        svgParts.push(`<text x="${node.x}" y="${node.y - 4}" text-anchor="middle" font-size="8" font-weight="bold" fill="white" font-family="Arial">SITE</text>`);
        svgParts.push(`<text x="${node.x}" y="${node.y + 8}" text-anchor="middle" font-size="8" fill="#C4BDD4" font-family="Arial">ACCESS</text>`);
      } else {
        svgParts.push(`<rect x="${node.x - NR}" y="${node.y - NR}" width="${NR * 2}" height="${NR * 2}" rx="2" fill="white" stroke="#7B6A96" stroke-width="2"/>`);
        if (node.label) {
          svgParts.push(`<text x="${node.x}" y="${node.y + 4}" text-anchor="middle" font-size="8" font-weight="bold" fill="#3D1A6E" font-family="Arial">${node.label.substring(0, 4).toUpperCase()}</text>`);
        }
      }
    });

    svgParts.push('</svg>');
    const svgString = svgParts.join('\n');
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width * 2; canvas.height = height * 2;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

export async function exportPDF({
  projectName, projectNumber, projectClient, projectPreparedBy, projectCheckedBy,
  selectedOAs, pinLocation, devSize,
  amArr, amDep, pmArr, pmDep, amTotal, pmTotal,
  totalPersonTrips, assignPeriod, amPeakTime, pmPeakTime,
  odFlows, modeShare, originMSOA, assignedNetwork, roadRadius,
  educationSchools,
  includeMap, destLayer, mapboxToken,
  includeSchematic, schematicNodes, schematicLinks, schematicSiteBox,
  junctionMatrices, modeAssumptions,
}) {
  // Destination zones vs zones that actually received an assigned car route.
  const routedCodes = new Set((assignedNetwork?.routes?.features || []).map(f => f.properties.destination));
  const totalZones = (odFlows && odFlows.flows) ? odFlows.flows.length : 0;
  const assignedZoneCount = (odFlows && odFlows.flows) ? odFlows.flows.filter(f => routedCodes.has(f.destination)).length : 0;

  // Pre-fetch images
  let mapImageDataUrl = null;
  let assignedZonesMapDataUrl = null;
  let schematicImageDataUrl = null;

  if (includeMap && destLayer) {
    try {
      mapImageDataUrl = await renderMapToDataUrl(destLayer, mapboxToken);
      if (routedCodes.size > 0) {
        assignedZonesMapDataUrl = await renderMapToDataUrl(destLayer, mapboxToken, 800, 500, routedCodes);
      }
    } catch (err) {
      console.error('Map image error:', err);
    }
  }

  if (includeSchematic && schematicNodes && schematicNodes.length > 0) {
    schematicImageDataUrl = await renderSchematicToDataUrl(schematicNodes, schematicLinks || [], schematicSiteBox);
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  let y = 0;

  // Header
  doc.setFillColor(61, 26, 110);
  doc.rect(0, 0, pageW, 42, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22); doc.setFont('helvetica', 'bold');
  doc.text('ODINFLOW', 14, 14);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('TRAVEL DISTRIBUTION SOFTWARE', 14, 20);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold');
  doc.text(projectName, pageW - 14, 12, { align: 'right' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
  const metaLine1Parts = [];
  if (projectNumber) metaLine1Parts.push(`Ref: ${projectNumber}`);
  if (projectClient) metaLine1Parts.push(`Client: ${projectClient}`);
  if (metaLine1Parts.length > 0) doc.text(metaLine1Parts.join('  |  '), pageW - 14, 19, { align: 'right' });
  const metaLine2Parts = [];
  if (projectPreparedBy) metaLine2Parts.push(`Prepared by: ${projectPreparedBy}`);
  if (projectCheckedBy) metaLine2Parts.push(`Checked by: ${projectCheckedBy}`);
  metaLine2Parts.push(`Date: ${formatDate()}`);
  doc.text(metaLine2Parts.join('  |  '), pageW - 14, 26, { align: 'right' });
  doc.setTextColor(196, 189, 212);
  doc.setFontSize(7);
  doc.text('TRAVEL DEMAND ASSESSMENT', 14, 35);
  y = 50;

  // Site Selection
  y = sectionHeader(doc, y, pageW, 'SITE SELECTION');
  doc.setTextColor(60, 60, 60); doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('Selected Output Area(s):', 14, y); y += 5;
  selectedOAs.forEach(oa => {
    doc.setFont('helvetica', 'bold'); doc.setTextColor(61, 26, 110);
    doc.text(oa, 20, y);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
    y += 5;
  });
  if (pinLocation) {
    y += 2;
    doc.text(`Site pin: ${pinLocation[0].toFixed(5)}, ${pinLocation[1].toFixed(5)}`, 14, y);
    y += 8;
  }

  // Destination zones map — its own full landscape page.
  if (mapImageDataUrl) {
    addLandscapeFigure(doc, 'DESTINATION ZONES MAP', mapImageDataUrl,
      'Map showing census trip destination zones. Darker shading indicates higher trip proportions. Map data © Mapbox © OpenStreetMap.');
    doc.addPage('a4', 'portrait'); y = 20;   // resume portrait for the following sections
  }


  // Trip Generation
  y = checkPage(doc, y, 60);
  y = sectionHeader(doc, y, pageW, 'TRIP GENERATION');
  const size = parseFloat(devSize) || 0;
  const tripGenBody = [
    ['Development Size', devSize ? `${devSize} units` : '—'], ['', ''],
    ['AM Peak hour', amPeakTime || '—'],
    ['AM Peak — Arrivals rate', amArr ? `${amArr} per unit` : '—'],
    ['AM Peak — Departures rate', amDep ? `${amDep} per unit` : '—'],
    ['AM Peak — Total person trips', amTotal ? `${amTotal} trips (${Math.round(amArr * size)} arr + ${Math.round(amDep * size)} dep)` : '—'],
    ['', ''],
    ['PM Peak hour', pmPeakTime || '—'],
    ['PM Peak — Arrivals rate', pmArr ? `${pmArr} per unit` : '—'],
    ['PM Peak — Departures rate', pmDep ? `${pmDep} per unit` : '—'],
    ['PM Peak — Total person trips', pmTotal ? `${pmTotal} trips (${Math.round(pmArr * size)} arr + ${Math.round(pmDep * size)} dep)` : '—'],
    ['', ''],
    ['Assignment period', assignPeriod ? assignPeriod.toUpperCase() : '—'],
    ['Trips for assignment', totalPersonTrips ? `${totalPersonTrips} person trips` : '—'],
  ];
  autoTable(doc, {
    startY: y, head: [], body: tripGenBody, theme: 'plain',
    styles: { fontSize: 9, cellPadding: 2 },
    columnStyles: { 0: { fontStyle: 'bold', textColor: [61, 26, 110], cellWidth: 80 }, 1: { textColor: [60, 60, 60] } },
    margin: { left: 14, right: 14 }
  });
  y = doc.lastAutoTable.finalY + 8;

  // Trip Distribution
  y = checkPage(doc, y, 40);
  y = sectionHeader(doc, y, pageW, 'TRIP DISTRIBUTION');
  if (odFlows && odFlows.flows) {
    doc.setTextColor(60, 60, 60); doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`Total census commuters from origin: ${odFlows.total_trips}`, 14, y); y += 6;
    if (assignedNetwork && assignedNetwork.routes) {
      doc.setFont('helvetica', 'bold'); doc.setTextColor(61, 26, 110);
      doc.text(`Destination zones: ${totalZones}   |   Zones assigned a car route: ${assignedZoneCount}`, 14, y);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60); y += 6;
    }
    autoTable(doc, {
      startY: y,
      head: [['#', 'Zone Code', 'Zone Name', 'Type', 'Share', 'Census', 'Dev Trips', 'Assigned']],
      body: odFlows.flows.map((f, i) => [i + 1, f.destination, f.name || '—', f.type ? f.type.toUpperCase() : '—', `${f.percentage}%`, f.count, totalPersonTrips ? Math.round(f.percentage / 100 * totalPersonTrips) : '—', routedCodes.has(f.destination) ? 'Yes' : '—']),
      theme: 'striped',
      headStyles: { fillColor: [61, 26, 110], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: [60, 60, 60] },
      alternateRowStyles: { fillColor: [245, 240, 250] },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 3) { data.cell.styles.textColor = data.cell.raw === 'LSOA' ? [13, 148, 136] : [123, 77, 191]; data.cell.styles.fontStyle = 'bold'; }
        if (data.section === 'body' && data.column.index === 6) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.textColor = [13, 148, 136]; }
        if (data.section === 'body' && data.column.index === 7) { data.cell.styles.halign = 'center'; if (data.cell.raw === 'Yes') { data.cell.styles.textColor = [15, 110, 86]; data.cell.styles.fontStyle = 'bold'; } else { data.cell.styles.textColor = [180, 180, 180]; } }
      },
      columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 26 }, 2: { cellWidth: 48 }, 3: { cellWidth: 13 }, 4: { cellWidth: 16 }, 5: { cellWidth: 16 }, 6: { cellWidth: 20, halign: 'right' }, 7: { cellWidth: 18 } },
      margin: { left: 14, right: 14 }
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Mode Share
  if (modeShare && modeShare.modes) {
    y = checkPage(doc, y, 60);
    y = sectionHeader(doc, y, pageW, 'MODE SHARE');
    if (originMSOA) {
      doc.setTextColor(60, 60, 60); doc.setFontSize(9); doc.setFont('helvetica', 'normal');
      doc.text(`Origin OA: ${originMSOA.oa}  |  MSOA: ${originMSOA.msoa} (${originMSOA.msoa_name})`, 14, y); y += 6;
    }
    autoTable(doc, {
      startY: y,
      head: [['Mode', 'Share', 'Census', 'Dev Trips']],
      body: modeShare.modes.map(m => [m.name, `${m.percentage}%`, m.count, totalPersonTrips && m.code === 7 ? Math.round(m.percentage / 100 * totalPersonTrips) : '—']),
      theme: 'striped',
      headStyles: { fillColor: [61, 26, 110], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: [60, 60, 60] },
      alternateRowStyles: { fillColor: [245, 240, 250] },
      didParseCell: (data) => {
        if (data.row.index !== undefined && modeShare.modes[data.row.index]?.code === 7) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.textColor = [61, 26, 110]; data.cell.styles.fillColor = [237, 229, 248]; }
      },
      columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 25 }, 2: { cellWidth: 25 }, 3: { cellWidth: 30 } },
      margin: { left: 14, right: 14 }
    });
    y = doc.lastAutoTable.finalY + 8;
    if (totalPersonTrips) {
      const carMode = modeShare.modes.find(m => m.code === 7);
      const vehicleTrips = carMode ? Math.round(carMode.percentage / 100 * totalPersonTrips) : 0;
      y = checkPage(doc, y, 24);
      doc.setFillColor(61, 26, 110); doc.rect(14, y, pageW - 28, 18, 'F');
      doc.setTextColor(196, 189, 212); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
      doc.text(`VEHICLE TRIPS FOR ASSIGNMENT (${assignPeriod ? assignPeriod.toUpperCase() : ''} PEAK)`, 17, y + 6);
      doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
      doc.text(`${vehicleTrips}`, 17, y + 14);
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(196, 189, 212);
      doc.text(`car driver trips from ${totalPersonTrips} person trips`, 35, y + 14);
      y += 24;
    }
  }

  // Trip Purpose & Education (school gravity distribution)
  const eduSchools = (educationSchools && educationSchools.schools ? educationSchools.schools : []).filter(s => (s.trips || 0) > 0);
  if (eduSchools.length > 0) {
    y = checkPage(doc, y, 60);
    y = sectionHeader(doc, y, pageW, 'TRIP PURPOSE — EDUCATION');
    const eduPersons = educationSchools.edu_trips || eduSchools.reduce((a, s) => a + (s.trips || 0), 0);
    const eduVeh = Math.round(eduPersons * EDUCATION_CAR_PCT / 100);
    doc.setTextColor(60, 60, 60); doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`${eduPersons} education person trips distributed to ${eduSchools.length} schools by gravity model (mean trip ${educationSchools.mean_trip_km} km).`, 14, y); y += 5;
    doc.text(`Education car share ${EDUCATION_CAR_PCT}% -> ${eduVeh} vehicle trips. Employment & Other use the census distribution and mode share above.`, 14, y); y += 6;
    autoTable(doc, {
      startY: y,
      head: [['School', 'Phase', 'Trips', 'Share', 'Dist (km)']],
      body: eduSchools.slice(0, 12).map(s => [s.name, (s.phase || '').replace('_', ' '), s.trips, `${Math.round((s.share || 0) * 1000) / 10}%`, s.distance_km]),
      theme: 'striped',
      headStyles: { fillColor: [13, 148, 136], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8, textColor: [60, 60, 60] },
      alternateRowStyles: { fillColor: [228, 245, 241] },
      columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 28 }, 2: { cellWidth: 22 }, 3: { cellWidth: 22 }, 4: { cellWidth: 24 } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
    const abp = assignedByPurpose(assignedNetwork);
    if (abp.total > 0) {
      y = checkPage(doc, y, 16);
      doc.setFillColor(245, 240, 250); doc.rect(14, y, pageW - 28, 12, 'F');
      doc.setTextColor(61, 26, 110); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.text(`ASSIGNED BY PURPOSE:  Employment + Other ${abp.census} veh (census zones)   |   Education ${abp.school} veh (schools)   |   Total ${abp.total} veh`, 17, y + 7.5);
      doc.setFont('helvetica', 'normal');
      y += 18;
    }
  }

  // Trip Assignment
  if (assignedNetwork) {
    y = checkPage(doc, y, 40);
    y = sectionHeader(doc, y, pageW, 'ROAD NETWORK & TRIP ASSIGNMENT');
    doc.setTextColor(60, 60, 60); doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text(`Assignment period: ${assignPeriod ? assignPeriod.toUpperCase() : '—'} peak`, 14, y); y += 5;
    doc.text(`AM peak hour: ${amPeakTime || '—'}  |  PM peak hour: ${pmPeakTime || '—'}`, 14, y); y += 5;
    doc.text(`Road links in study area: ${assignedNetwork.edge_count}`, 14, y); y += 5;
    doc.text(`Total vehicle trips assigned: ${assignedNetwork.total_assigned}`, 14, y); y += 8;
    if (assignedNetwork.routes && assignedNetwork.routes.features && assignedNetwork.routes.features.length > 0) {
      const sortedRoutes = [...assignedNetwork.routes.features].sort((a, b) => b.properties.trips - a.properties.trips);
      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(61, 26, 110);
      doc.text('Trip assignment by destination:', 14, y); y += 4;
      autoTable(doc, {
        startY: y,
        head: [['Destination Code', 'Destination Name', 'Type', 'Census Share', 'Vehicle Trips']],
        body: sortedRoutes.map(f => {
          const dest = f.properties.destination || '';
          const flow = odFlows?.flows?.find(fl => fl.destination === dest);
          return [dest, flow?.name || '—', flow?.type ? flow.type.toUpperCase() : '—', flow?.percentage ? `${flow.percentage}%` : '—', f.properties.trips];
        }),
        theme: 'striped',
        headStyles: { fillColor: [61, 26, 110], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: [60, 60, 60] },
        alternateRowStyles: { fillColor: [245, 240, 250] },
        didParseCell: (data) => {
          if (data.section === 'body' && data.column.index === 2) { data.cell.styles.textColor = data.cell.raw === 'LSOA' ? [13, 148, 136] : [123, 77, 191]; data.cell.styles.fontStyle = 'bold'; }
          if (data.section === 'body' && data.column.index === 4) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.textColor = [232, 93, 0]; }
        },
        columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 72 }, 2: { cellWidth: 14 }, 3: { cellWidth: 20, halign: 'right' }, 4: { cellWidth: 22, halign: 'right' } },
        margin: { left: 14, right: 14 }
      });
      y = doc.lastAutoTable.finalY + 8;
    }
  }

  // Assigned destination zones plan — placed after the assignment section since it
  // shows the result of assignment. Teal = zones that received a car route.
  if (assignedZonesMapDataUrl) {
    addLandscapeFigure(doc, 'ASSIGNED DESTINATION ZONES', assignedZonesMapDataUrl,
      `${assignedZoneCount} of ${totalZones} destination zones received an assigned car route (teal). Grey zones generated less than one whole car trip. Map data © Mapbox © OpenStreetMap.`);
    doc.addPage('a4', 'portrait'); y = 20;
  }

  // Mode-share assumptions — areas whose trips were reassigned across modes.
  if (modeAssumptions && modeAssumptions.length > 0) {
    if (y > 235) { doc.addPage(); y = 20; }
    y = sectionHeader(doc, y, pageW, 'MODE-SHARE ASSUMPTIONS');
    doc.setFontSize(8); doc.setTextColor(90, 90, 90); doc.setFont('helvetica', 'normal');
    doc.text('Trips to the areas below were reassigned from the standard census mode split (e.g. rail-served', 14, y);
    doc.text('destinations). Only the car proportion is assigned to the road network.', 14, y + 4);
    y += 10;
    autoTable(doc, {
      startY: y,
      head: [['Area', 'Car %', 'Rail %', 'Bus %', 'Cycle %', 'Walk %', 'Other %', 'Car trips']],
      body: modeAssumptions.map(a => [a.lad, a.split.car, a.split.rail, a.split.bus, a.split.cycle, a.split.walk, a.split.other, a.carTrips == null ? '—' : a.carTrips]),
      styles: { fontSize: 8, halign: 'center', cellPadding: 1.6, lineColor: [237, 229, 248], lineWidth: 0.1 },
      headStyles: { fillColor: [61, 26, 110], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 0: { halign: 'left', cellWidth: 44 } },
      margin: { left: 14, right: 14 },
      theme: 'grid',
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Schematic diagram — its own full landscape page.
  if (schematicImageDataUrl) {
    addLandscapeFigure(doc, 'SCHEMATIC DIAGRAM', schematicImageDataUrl,
      'Schematic showing junction layout and link flows. Purple boxes = arrivals (nearside). Teal boxes = departures (offside).');
  }

  // Junction turning matrices — a diagram + turning matrix per junction.
  if (junctionMatrices && junctionMatrices.length > 0) {
    doc.addPage('a4', 'portrait'); y = 20;
    y = sectionHeader(doc, y, pageW, 'JUNCTION TURNING MATRICES');
    const imgSize = 52;
    for (const jm of junctionMatrices) {
      if (y + imgSize + 12 > 275) { doc.addPage(); y = 20; }
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(61, 26, 110);
      doc.text(jm.label || 'Junction', 14, y);
      const blockTop = y + 3;
      if (jm.diagramPng) doc.addImage(jm.diagramPng, 'PNG', 14, blockTop, imgSize, imgSize);

      const head = [['From \\ To', ...jm.armNames, 'Total']];
      const body = jm.armNames.map(f => {
        const vals = jm.armNames.map(t => (jm.matrix[f] && jm.matrix[f][t]) || 0);
        return [f, ...vals, vals.reduce((s, v) => s + v, 0)];
      });
      const colTotals = jm.armNames.map(t => jm.armNames.reduce((s, f) => s + ((jm.matrix[f] && jm.matrix[f][t]) || 0), 0));
      body.push(['Total', ...colTotals, colTotals.reduce((s, v) => s + v, 0)]);
      autoTable(doc, {
        head, body, startY: blockTop,
        margin: { left: 14 + imgSize + 6, right: 14 },
        styles: { fontSize: 8, halign: 'center', cellPadding: 1.6, lineColor: [237, 229, 248], lineWidth: 0.1 },
        headStyles: { fillColor: [61, 26, 110], textColor: 255, fontStyle: 'bold' },
        theme: 'grid',
      });
      y = Math.max(blockTop + imgSize, doc.lastAutoTable.finalY) + 10;
    }
    doc.setFont('helvetica', 'normal');
  }

  // Footer — computed per page so it sits correctly on both portrait and
  // landscape (figure) pages.
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const w = doc.internal.pageSize.getWidth();
    const hh = doc.internal.pageSize.getHeight();
    doc.setFillColor(61, 26, 110); doc.rect(0, hh - 10, w, 10, 'F');
    doc.setTextColor(196, 189, 212); doc.setFontSize(7); doc.setFont('helvetica', 'normal');
    doc.text('ODINFLOW — Travel Distribution Software — odinflow.co.uk', 14, hh - 4);
    doc.text(`Page ${i} of ${pageCount}`, w - 14, hh - 4, { align: 'right' });
  }

  doc.save(`${projectName.replace(/\s+/g, '_')}_ODIN_Report.pdf`);
}

export function exportExcel({
  projectName, selectedOAs, pinLocation, devSize,
  amArr, amDep, pmArr, pmDep, amTotal, pmTotal,
  totalPersonTrips, assignPeriod, amPeakTime, pmPeakTime,
  odFlows, modeShare, originMSOA, assignedNetwork, roadRadius, educationSchools
}) {
  const wb = XLSX.utils.book_new();
  const size = parseFloat(devSize) || 0;

  const summaryData = [
    ['ODINFLOW — Travel Distribution Software'], [''],
    ['Project', projectName], ['Generated', formatDate()], [''],
    ['SITE SELECTION'],
    ['Selected OA(s)', selectedOAs.join(', ')],
    ['Pin Location', pinLocation ? `${pinLocation[0].toFixed(5)}, ${pinLocation[1].toFixed(5)}` : '—'],
    [''], ['TRIP GENERATION'],
    ['Development Size', devSize ? `${devSize} units` : '—'], [''],
    ['AM Peak hour', amPeakTime || '—'],
    ['AM Peak — Arrivals rate', amArr || '—'], ['AM Peak — Departures rate', amDep || '—'],
    ['AM Peak — Arrivals (trips)', amArr && size ? Math.round(amArr * size) : '—'],
    ['AM Peak — Departures (trips)', amDep && size ? Math.round(amDep * size) : '—'],
    ['AM Peak — Total person trips', amTotal || '—'], [''],
    ['PM Peak hour', pmPeakTime || '—'],
    ['PM Peak — Arrivals rate', pmArr || '—'], ['PM Peak — Departures rate', pmDep || '—'],
    ['PM Peak — Arrivals (trips)', pmArr && size ? Math.round(pmArr * size) : '—'],
    ['PM Peak — Departures (trips)', pmDep && size ? Math.round(pmDep * size) : '—'],
    ['PM Peak — Total person trips', pmTotal || '—'], [''],
    ['Assignment period', assignPeriod ? assignPeriod.toUpperCase() : '—'],
    ['Trips for assignment', totalPersonTrips || '—'], [''],
    ['MODE SHARE'],
    ['Origin OA', originMSOA ? originMSOA.oa : '—'],
    ['Origin LSOA', originMSOA ? `${originMSOA.lsoa} (${originMSOA.lsoa_name})` : '—'],
    ['Origin MSOA', originMSOA ? `${originMSOA.msoa} (${originMSOA.msoa_name})` : '—'],
  ];

  if (modeShare && modeShare.modes) {
    summaryData.push(['']);
    modeShare.modes.forEach(m => {
      const devTrips = totalPersonTrips ? Math.round(m.percentage / 100 * totalPersonTrips) : '—';
      summaryData.push([m.name, `${m.percentage}%`, `${m.count} census`, m.code === 7 ? `${devTrips} vehicle trips` : '']);
    });
    const carMode = modeShare.modes.find(m => m.code === 7);
    if (carMode && totalPersonTrips) {
      summaryData.push(['']);
      summaryData.push([`VEHICLE TRIPS FOR ASSIGNMENT (${assignPeriod ? assignPeriod.toUpperCase() : ''} PEAK)`, Math.round(carMode.percentage / 100 * totalPersonTrips)]);
    }
  }

  if (assignedNetwork) {
    summaryData.push([''], ['ROAD NETWORK & TRIP ASSIGNMENT']);
    summaryData.push(['Assignment Period', assignPeriod ? assignPeriod.toUpperCase() : '—']);
    summaryData.push(['AM Peak Hour', amPeakTime || '—'], ['PM Peak Hour', pmPeakTime || '—']);
    summaryData.push(['Road Links in Study Area', assignedNetwork.edge_count]);
    summaryData.push(['Routes Assigned', assignedNetwork.route_count || '—']);
    summaryData.push(['Total Trips Assigned', assignedNetwork.total_assigned]);
  }

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
  wsSummary['!cols'] = [{ wch: 40 }, { wch: 30 }, { wch: 20 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  if (odFlows && odFlows.flows) {
    const wsDistribution = XLSX.utils.aoa_to_sheet([
      ['ODINFLOW — Trip Distribution'], ['Project:', projectName],
      ['Origin OA(s):', selectedOAs.join(', ')], ['Total Census Commuters:', odFlows.total_trips], [''],
      ['#', 'Zone Code', 'Zone Name', 'Type', 'Census Count', 'Share (%)', 'Development Trips'],
      ...odFlows.flows.map((f, i) => [i + 1, f.destination, f.name || '—', f.type ? f.type.toUpperCase() : '—', f.count, f.percentage, totalPersonTrips ? Math.round(f.percentage / 100 * totalPersonTrips) : '—'])
    ]);
    wsDistribution['!cols'] = [{ wch: 6 }, { wch: 16 }, { wch: 35 }, { wch: 8 }, { wch: 14 }, { wch: 12 }, { wch: 18 }];
    XLSX.utils.book_append_sheet(wb, wsDistribution, 'Trip Distribution');
  }

  if (modeShare && modeShare.modes) {
    const carMode = modeShare.modes.find(m => m.code === 7);
    const vehicleTrips = carMode && totalPersonTrips ? Math.round(carMode.percentage / 100 * totalPersonTrips) : '—';
    const wsMode = XLSX.utils.aoa_to_sheet([
      ['ODINFLOW — Mode Share'], ['Project:', projectName],
      ['Origin OA:', originMSOA ? originMSOA.oa : '—'],
      ['Origin LSOA:', originMSOA ? `${originMSOA.lsoa} (${originMSOA.lsoa_name})` : '—'],
      ['Origin MSOA:', originMSOA ? `${originMSOA.msoa} (${originMSOA.msoa_name})` : '—'],
      ['Total Census Commuters:', modeShare.total],
      ['Assignment Period:', assignPeriod ? assignPeriod.toUpperCase() : '—'],
      ['Person Trips for Assignment:', totalPersonTrips || '—'],
      ['Vehicle Trips for Assignment:', vehicleTrips], [''],
      ['Mode', 'Census Count', 'Share (%)', 'Development Trips'],
      ...modeShare.modes.map(m => [m.name, m.count, m.percentage, totalPersonTrips && m.code === 7 ? Math.round(m.percentage / 100 * totalPersonTrips) : ''])
    ]);
    wsMode['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 12 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, wsMode, 'Mode Share');
  }

  if (assignedNetwork) {
    const routeRows = assignedNetwork.routes?.features
      ? [...assignedNetwork.routes.features].sort((a, b) => b.properties.trips - a.properties.trips).map(f => {
          const dest = f.properties.destination || '';
          const flow = odFlows?.flows?.find(fl => fl.destination === dest);
          return [dest, flow?.name || '—', flow?.type ? flow.type.toUpperCase() : '—', flow?.percentage || '—', f.properties.trips];
        })
      : [];
    const wsAssignment = XLSX.utils.aoa_to_sheet([
      ['ODINFLOW — Trip Assignment'], ['Project:', projectName],
      ['Assignment Period:', assignPeriod ? assignPeriod.toUpperCase() : '—'],
      ['AM Peak Hour:', amPeakTime || '—'], ['PM Peak Hour:', pmPeakTime || '—'],
      ['Total Trips Assigned:', assignedNetwork.total_assigned],
      ['Routes:', assignedNetwork.route_count || routeRows.length],
      ['Road Links in Study Area:', assignedNetwork.edge_count], [''],
      ['Destination Code', 'Destination Name', 'Type', 'Census Share (%)', 'Vehicle Trips'],
      ...routeRows
    ]);
    wsAssignment['!cols'] = [{ wch: 18 }, { wch: 40 }, { wch: 8 }, { wch: 18 }, { wch: 16 }];
    XLSX.utils.book_append_sheet(wb, wsAssignment, 'Trip Assignment');
  }

  // Trip Purpose — Education (school gravity distribution) + assignment split
  const eduSchools = (educationSchools && educationSchools.schools ? educationSchools.schools : []).filter(s => (s.trips || 0) > 0);
  if (eduSchools.length > 0) {
    const eduPersons = educationSchools.edu_trips || eduSchools.reduce((a, s) => a + (s.trips || 0), 0);
    const eduVeh = Math.round(eduPersons * EDUCATION_CAR_PCT / 100);
    const abp = assignedByPurpose(assignedNetwork);
    const rows = [
      ['ODINFLOW — Trip Purpose (Education)'], ['Project:', projectName], [''],
      ['Education person trips:', eduPersons],
      ['Schools receiving trips:', eduSchools.length],
      ['Mean trip length (km):', educationSchools.mean_trip_km],
      ['Education car share (%):', EDUCATION_CAR_PCT],
      ['Education vehicle trips:', eduVeh], [''],
      ['Assigned by purpose (vehicle trips):'],
      ['  Employment + Other (census zones):', abp.census],
      ['  Education (schools):', abp.school],
      ['  Total assigned:', abp.total], [''],
      ['School', 'Phase', 'Trips', 'Share (%)', 'Distance (km)'],
      ...eduSchools.map(s => [s.name, (s.phase || '').replace('_', ' '), s.trips, Math.round((s.share || 0) * 1000) / 10, s.distance_km]),
    ];
    const wsPurpose = XLSX.utils.aoa_to_sheet(rows);
    wsPurpose['!cols'] = [{ wch: 42 }, { wch: 14 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsPurpose, 'Trip Purpose');
  }

  XLSX.writeFile(wb, `${projectName.replace(/\s+/g, '_')}_ODIN_Export.xlsx`);
}

async function renderActiveTravelMap(atResults, mode, mapboxToken, width = 1280, height = 850) {
  try {
    // Get bounds from largest isochrone
    const isoByMinutes = atResults.isochroneResults[mode];
    if (!isoByMinutes) return null;
    // Support both old single-object format and new keyed-by-minutes format
    const iso = isoByMinutes.geometry
      ? isoByMinutes
      : Object.values(isoByMinutes).sort((a, b) => (b.properties?.minutes || 0) - (a.properties?.minutes || 0))[0];
    if (!iso?.geometry) return null;

    const coords = iso.geometry.coordinates[0];
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    coords.forEach(([lng, lat]) => {
      if (lng < minLng) minLng = lng;
      if (lat < minLat) minLat = lat;
      if (lng > maxLng) maxLng = lng;
      if (lat > maxLat) maxLat = lat;
    });

    const padLng = (maxLng - minLng) * 0.1;
    const padLat = (maxLat - minLat) * 0.1;
    const bbox = `[${minLng - padLng},${minLat - padLat},${maxLng + padLng},${maxLat + padLat}]`;
    const baseUrl = `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/${bbox}/${width}x${height}?access_token=${mapboxToken}`;

    const response = await fetch(baseUrl);
    const blob = await response.blob();
    const baseImageDataUrl = await new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });

    const bounds = { minLng: minLng - padLng, minLat: minLat - padLat, maxLng: maxLng + padLng, maxLat: maxLat + padLat };

    return new Promise(resolve => {
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      const baseImg = new Image();
      baseImg.onload = () => {
        // Use actual image dimensions to avoid squish
        const actualW = baseImg.naturalWidth || width;
        const actualH = baseImg.naturalHeight || height;
        canvas.width = actualW;
        canvas.height = actualH;
        // Update width/height references for all subsequent drawing
        const W = actualW, H = actualH;
        ctx.drawImage(baseImg, 0, 0, W, H);

        const toLngLatXY = (lng, lat) => {
          const x = ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * W;
          const y = (1 - (lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * H;
          return [x, y];
        };

        const color = mode === 'walking' ? '#3D1A6E' : '#0D9488';

        // 1. Draw all isochrones largest to smallest — each as a filled band
        const allIsoEntries = isoByMinutes.geometry
          ? [[iso.properties?.minutes || 0, iso]]
          : Object.entries(isoByMinutes).sort((a, b) => Number(b[0]) - Number(a[0]));
        const fillColor = mode === 'walking' ? 'rgba(61,26,110,0.12)' : 'rgba(13,148,136,0.12)';
        // Draw fills largest to smallest so inner ones paint over outer
        allIsoEntries.forEach(([mins, isoItem]) => {
          if (!isoItem?.geometry) return;
          const isoCoords = isoItem.geometry.coordinates[0];
          ctx.beginPath();
          isoCoords.forEach(([lng, lat], i) => {
            const [x, y] = toLngLatXY(lng, lat);
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.fillStyle = fillColor;
          ctx.fill();
        });
        // Draw outlines separately so all are visible
        allIsoEntries.forEach(([mins, isoItem]) => {
          if (!isoItem?.geometry) return;
          const isoCoords = isoItem.geometry.coordinates[0];
          ctx.beginPath();
          isoCoords.forEach(([lng, lat], i) => {
            const [x, y] = toLngLatXY(lng, lat);
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.strokeStyle = color;
          ctx.setLineDash([6, 4]);
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.setLineDash([]);
        });

        // 2. Draw site boundary (red) or study area (purple dashed)
        if (atResults.siteBoundary && atResults.siteBoundary.length >= 3) {
          ctx.beginPath();
          atResults.siteBoundary.forEach(([lat, lng], i) => {
            const [x, y] = toLngLatXY(lng, lat);
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.fillStyle = 'rgba(232,0,28,0.08)';
          ctx.fill();
          ctx.strokeStyle = '#E8001C';
          ctx.lineWidth = 2.5;
          ctx.setLineDash([8, 4]);
          ctx.stroke();
          ctx.setLineDash([]);
        }

        // 2b. Draw site pin
        if (atResults.pinLocation) {
          const [pinLat, pinLng] = atResults.pinLocation;
          const [px, py] = toLngLatXY(pinLng, pinLat);
          // Red teardrop pin
          ctx.beginPath();
          ctx.arc(px, py - 10, 8, 0, Math.PI * 2);
          ctx.fillStyle = '#E8001C';
          ctx.fill();
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(px - 6, py - 4);
          ctx.lineTo(px, py + 4);
          ctx.lineTo(px + 6, py - 4);
          ctx.fillStyle = '#E8001C';
          ctx.fill();
          // White dot in centre
          ctx.beginPath();
          ctx.arc(px, py - 10, 3, 0, Math.PI * 2);
          ctx.fillStyle = 'white';
          ctx.fill();
        }

        // 3. Draw routes
        const routes = atResults.routeResults[mode]?.routes?.features || [];
        routes.forEach(feature => {
          const routeCoords = feature.geometry.coordinates;
          ctx.beginPath();
          routeCoords.forEach(([lng, lat], i) => {
            const [x, y] = toLngLatXY(lng, lat);
            i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5;
          ctx.globalAlpha = 0.6;
          ctx.stroke();
          ctx.globalAlpha = 1;
        });

        // 4. Draw destination markers
        const typeColors = {
          school: '#3D1A6E', hospital: '#DC2626', gp: '#EA580C',
          pharmacy: '#7C3AED', supermarket: '#0284C7', park: '#16A34A',
          leisure: '#0D9488', train_station: '#1D4ED8', bus_stop: '#92400E'
        };
        const dests = atResults.destinationResults[mode] || [];
        dests.forEach(dest => {
          const [x, y] = toLngLatXY(dest.lng, dest.lat);
          const dc = typeColors[dest.type] || '#666666';
          ctx.beginPath();
          ctx.arc(x, y, 6, 0, Math.PI * 2);
          ctx.fillStyle = dc;
          ctx.fill();
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        });

        // 5. Draw legend
        const legendX = 20;
        const legendY = H - 260;
        const hasSiteBoundary = atResults.siteBoundary && atResults.siteBoundary.length >= 3;
        const presentTypes = [...new Set(dests.map(d => d.type))].slice(0, 4);
        

        // Build isochrone entries sorted smallest to largest for legend
        const isoMinutesSorted = isoByMinutes.geometry
          ? [iso.properties?.minutes || atResults.minutes]
          : Object.keys(isoByMinutes).map(Number).sort((a,b) => a - b);
        const modeLabel2 = mode === 'walking' ? 'Walking' : 'Cycling';

        // Calculate legend height dynamically
        const legendItemCount = isoMinutesSorted.length + 1 + (atResults.pinLocation ? 1 : 0) + (hasSiteBoundary ? 1 : 0) + presentTypes.length;
        const legendH2 = 36 + legendItemCount * 22;

        // Redraw legend background with correct size
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.strokeStyle = '#E5E7EB';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(legendX, legendY, 260, legendH2, 6);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#1C1C2E';
        ctx.font = 'bold 13px Arial';
        ctx.fillText('Legend', legendX + 12, legendY + 22);

        let nextY = legendY + 38;

        // One box per isochrone threshold
        const isoOpacities = isoMinutesSorted.length === 1 ? [0.12] : isoMinutesSorted.map((_, i) => 0.06 + (i / (isoMinutesSorted.length - 1)) * 0.14);
        isoMinutesSorted.forEach((mins, idx) => {
          ctx.fillStyle = mode === 'walking'
            ? `rgba(61,26,110,${isoOpacities[idx]})`
            : `rgba(13,148,136,${isoOpacities[idx]})`;
          ctx.fillRect(legendX + 12, nextY, 32, 14);
          ctx.beginPath();
          ctx.setLineDash([5, 3]);
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.5;
          ctx.strokeRect(legendX + 12, nextY, 32, 14);
          ctx.setLineDash([]);
          ctx.fillStyle = '#1C1C2E';
          ctx.font = '12px Arial';
          ctx.fillText(`${modeLabel2} isochrone (${mins} min)`, legendX + 52, nextY + 11);
          nextY += 22;
        });

        // Routes — line centred in the 32px symbol column (legendX+12 to legendX+44)
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = 0.8;
        ctx.moveTo(legendX + 12, nextY + 7);
        ctx.lineTo(legendX + 44, nextY + 7);
        ctx.stroke();
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#1C1C2E';
        ctx.font = '12px Arial';
        ctx.fillText('Routes', legendX + 52, nextY + 11);
        nextY += 22;

        // Site pin — circle centred in symbol column
        if (atResults.pinLocation) {
          ctx.beginPath();
          ctx.arc(legendX + 28, nextY + 7, 7, 0, Math.PI * 2);
          ctx.fillStyle = '#E8001C';
          ctx.fill();
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.fillStyle = '#1C1C2E';
          ctx.font = '12px Arial';
          ctx.fillText('Site location', legendX + 52, nextY + 11);
          nextY += 22;
        }

        // Site boundary
        if (hasSiteBoundary) {
          ctx.fillStyle = 'rgba(232,0,28,0.08)';
          ctx.fillRect(legendX + 12, nextY, 32, 14);
          ctx.beginPath();
          ctx.setLineDash([6, 4]);
          ctx.strokeStyle = '#E8001C';
          ctx.lineWidth = 1.5;
          ctx.strokeRect(legendX + 12, nextY, 32, 14);
          ctx.setLineDash([]);
          ctx.fillStyle = '#1C1C2E';
          ctx.font = '12px Arial';
          ctx.fillText('Site boundary', legendX + 52, nextY + 11);
          nextY += 22;
        }

        // Destination types
        const legendTypeColors = {
          school: '#3D1A6E', hospital: '#DC2626', gp: '#EA580C',
          pharmacy: '#7C3AED', supermarket: '#0284C7', park: '#16A34A',
          leisure: '#0D9488', train_station: '#1D4ED8', bus_stop: '#92400E'
        };
        const typeLabels = {
          school: 'School', hospital: 'Hospital', gp: 'GP / Health',
          pharmacy: 'Pharmacy', supermarket: 'Supermarket', park: 'Park',
          leisure: 'Leisure', train_station: 'Railway Station', bus_stop: 'Bus Station'
        };
        
        presentTypes.forEach((type) => {
          // Coloured circle
          ctx.beginPath();
          ctx.arc(legendX + 28, nextY + 7, 7, 0, Math.PI * 2);
          ctx.fillStyle = legendTypeColors[type] || '#666';
          ctx.fill();
          ctx.strokeStyle = 'white';
          ctx.lineWidth = 1.5;
          ctx.stroke();
          // White initial letter inside circle
          ctx.font = 'bold 8px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'white';
          const initial = (typeLabels[type] || type).charAt(0).toUpperCase();
          ctx.fillText(initial, legendX + 28, nextY + 7);
          ctx.textAlign = 'left';
          ctx.textBaseline = 'alphabetic';
          // Label
          ctx.fillStyle = '#1C1C2E';
          ctx.font = '12px Arial';
          ctx.fillText(typeLabels[type] || type, legendX + 52, nextY + 11);
          nextY += 22;
        });

        // 6. Attribution bar
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillRect(0, H - 22, W, 22);
        ctx.fillStyle = '#6B6880';
        ctx.font = '11px Arial';
        ctx.fillText('© Crown copyright and database rights 2026 OS  |  © Mapbox  |  Generated by ODINFLOW', 10, H - 7);

        // 7. North arrow
        const arrowX = W - 52;
        const arrowY = H - 100;
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.strokeStyle = '#EDE5F8';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(arrowX, arrowY, 30, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.beginPath();
        ctx.strokeStyle = '#1C1C2E';
        ctx.lineWidth = 2;
        ctx.moveTo(arrowX, arrowY + 18);
        ctx.lineTo(arrowX, arrowY - 18);
        ctx.stroke();
        ctx.beginPath();
        ctx.fillStyle = '#1C1C2E';
        ctx.moveTo(arrowX, arrowY - 20);
        ctx.lineTo(arrowX - 8, arrowY - 4);
        ctx.lineTo(arrowX, arrowY);
        ctx.closePath();
        ctx.fill();
        ctx.beginPath();
        ctx.fillStyle = 'white';
        ctx.strokeStyle = '#1C1C2E';
        ctx.lineWidth = 1;
        ctx.moveTo(arrowX, arrowY - 20);
        ctx.lineTo(arrowX + 8, arrowY - 4);
        ctx.lineTo(arrowX, arrowY);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = '#1C1C2E';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('N', arrowX, arrowY + 32);
        ctx.textAlign = 'left';

        resolve(canvas.toDataURL('image/png'));
      };
      baseImg.onerror = () => resolve(null);
      baseImg.src = baseImageDataUrl;
    });
  } catch (err) {
    console.error('Active travel map render error:', err);
    return null;
  }
}

export async function exportActiveTravelPDF({
  projectName, projectNumber, projectClient, projectPreparedBy, projectCheckedBy,
  atResults, mapboxToken,
}) {
  if (!atResults) return;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  const drawHeader = (title, subtitle, color = [61, 26, 110]) => {
    doc.setFillColor(...color);
    doc.rect(0, 0, pageW, 42, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22); doc.setFont('helvetica', 'bold');
    doc.text('ODINFLOW', 14, 14);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.text('ACTIVE TRAVEL ACCESSIBILITY ANALYSIS', 14, 20);
    doc.setFontSize(11); doc.setFont('helvetica', 'bold');
    doc.text(projectName || 'Untitled Project', pageW - 14, 12, { align: 'right' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    const metaParts = [];
    if (projectNumber) metaParts.push(`Ref: ${projectNumber}`);
    if (projectClient) metaParts.push(`Client: ${projectClient}`);
    if (metaParts.length > 0) doc.text(metaParts.join('  |  '), pageW - 14, 19, { align: 'right' });
    const metaParts2 = [];
    if (projectPreparedBy) metaParts2.push(`Prepared by: ${projectPreparedBy}`);
    if (projectCheckedBy) metaParts2.push(`Checked by: ${projectCheckedBy}`);
    metaParts2.push(`Date: ${formatDate()}`);
    doc.text(metaParts2.join('  |  '), pageW - 14, 26, { align: 'right' });
    doc.setTextColor(196, 189, 212);
    doc.setFontSize(7);
    doc.text(title.toUpperCase(), 14, 35);
  };

  const drawSummaryTable = (mode, y) => {
    const dests = atResults.destinationResults[mode] || [];
    const routes = atResults.routeResults[mode]?.routes?.features || [];
    const typeLabels = {
      school: 'School', hospital: 'Hospital', gp: 'GP / Health Centre',
      pharmacy: 'Pharmacy', supermarket: 'Supermarket', park: 'Park / Open Space',
      leisure: 'Leisure Centre', train_station: 'Railway Station', bus_stop: 'Bus Station'
    };

    const rows = dests.map((dest) => {
      const route = routes.find(r =>
        Math.abs((r.properties.dest_lat || 0) - dest.lat) < 0.0005 &&
        Math.abs((r.properties.dest_lng || 0) - dest.lng) < 0.0005
      );
      const duration = route ? `${route.properties.duration_min} min` : '—';
      const distance = route ? `${Math.round(route.properties.distance_m)}m` : '—';
      return [
        dest.name || dest.description,
        typeLabels[dest.type] || dest.type,
        duration,
        distance,
      ];
    }).sort((a, b) => {
      const aMin = parseFloat(a[2]) || 999;
      const bMin = parseFloat(b[2]) || 999;
      return aMin - bMin;
    });

    if (rows.length === 0) {
      doc.setFontSize(9); doc.setTextColor(107, 104, 128);
      doc.text('No destinations found within the isochrone.', 14, y);
      return y + 10;
    }

    autoTable(doc, {
      startY: y,
      head: [['Destination', 'Type', `${mode === 'walking' ? 'Walking' : 'Cycling'} Time`, 'Distance']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: mode === 'walking' ? [61, 26, 110] : [13, 148, 136],
        textColor: 255, fontSize: 8, fontStyle: 'bold'
      },
      bodyStyles: { fontSize: 8, textColor: [60, 60, 60] },
      alternateRowStyles: { fillColor: [249, 247, 253] },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 50 },
        2: { cellWidth: 28 },
        3: { cellWidth: 28 },
      },
      margin: { left: 14, right: 14 },
    });

    return doc.lastAutoTable.finalY + 8;
  };

  const modes = Object.keys(atResults.isochroneResults);

  for (let mi = 0; mi < modes.length; mi++) {
    const mode = modes[mi];
    const color = mode === 'walking' ? [61, 26, 110] : [13, 148, 136];
    const modeLabel = mode === 'walking' ? 'Walking' : 'Cycling';
    const emoji = mode === 'walking' ? '🚶' : '🚴';

    if (mi > 0) doc.addPage();

    // Header
    drawHeader(`${emoji} ${modeLabel} Accessibility Analysis`, '', color);

    let y = 50;

    // Analysis parameters
    y = sectionHeader(doc, y, pageW, `${modeLabel.toUpperCase()} CATCHMENT PARAMETERS`);
    doc.setFontSize(9); doc.setTextColor(60, 60, 60); doc.setFont('helvetica', 'normal');
    doc.text(`Time threshold: ${atResults.minutes} minutes ${modeLabel.toLowerCase()}`, 14, y); y += 5;
    const destCount = (atResults.destinationResults[mode] || []).length;
    doc.text(`Destinations found within catchment: ${destCount}`, 14, y); y += 5;
    doc.setFontSize(8); doc.setTextColor(107, 104, 128);
    doc.text('Note: Routing uses OSM-derived path data and may not include all Public Rights of Way and bridleways.', 14, y); y += 10;

    // Map — full page
    doc.addPage();
    y = 14;
    y = sectionHeader(doc, y, pageW, `${modeLabel.toUpperCase()} ACCESSIBILITY MAP`);
    const mapImg = await renderActiveTravelMap(atResults, mode, mapboxToken, 1280, 850);
    if (mapImg) {
      const mapW = pageW - 28;
      const mapH = mapW * (850 / 1280);
      doc.addImage(mapImg, 'PNG', 14, y, mapW, mapH);
      y += mapH + 4;
      doc.setFontSize(7); doc.setTextColor(107, 104, 128);
      doc.setFillColor(...color);
      doc.rect(14, y, 3, 3, 'F');
      const minutesListPdf = atResults.minutesArray ? atResults.minutesArray.join('/') : atResults.minutes;
      doc.text(`${modeLabel} isochrone (${minutesListPdf} min)`, 20, y + 2.5);
      doc.text('© Crown copyright and database rights 2026 OS  |  © Mapbox', pageW - 14, y + 2.5, { align: 'right' });
      y += 8;
    }

    // Summary table — new page
    doc.addPage();
    y = 14;
    y = sectionHeader(doc, y, pageW, `${modeLabel.toUpperCase()} DESTINATION SUMMARY`);
    drawSummaryTable(mode, y);
  }

  // Footer on each page
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFillColor(245, 240, 250);
    doc.rect(0, 287, pageW, 10, 'F');
    doc.setFontSize(7); doc.setTextColor(107, 104, 128);
    doc.text('Generated by ODINFLOW  |  odinflow.co.uk', 14, 293);
    doc.text(`Page ${i} of ${pageCount}`, pageW - 14, 293, { align: 'right' });
  }

  doc.save(`${(projectName || 'Project').replace(/\s+/g, '_')}_Active_Travel_Analysis.pdf`);
}

export async function exportActiveTravelJPEG({ atResults, mapboxToken }) {
  if (!atResults) return;
  const modes = Object.keys(atResults.isochroneResults || {});
  if (modes.length === 0) { alert('No results to export.'); return; }

  for (const mode of modes) {
    try {
      const isoByMinutes = atResults.isochroneResults[mode];
      const mapImg = await renderActiveTravelMap(atResults, mode, mapboxToken, 1280, 850);
      if (!mapImg) { console.error('No map image for mode:', mode); continue; }

      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = 1280;
            canvas.height = 850;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, 1280, 850);
            ctx.drawImage(img, 0, 0, 1280, 850);
            const jpeg = canvas.toDataURL('image/jpeg', 0.95);
            const link = document.createElement('a');
            const minutesListJpeg = isoByMinutes.geometry
              ? String(atResults.minutes)
              : Object.keys(isoByMinutes).map(Number).sort((a,b)=>a-b).join('-');
            link.download = `ODINFLOW_ActiveTravel_${mode}_${minutesListJpeg}min.jpg`;
            link.href = jpeg;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            resolve();
          } catch(e) { reject(e); }
        };
        img.onerror = reject;
        img.src = mapImg;
      });

      await new Promise(r => setTimeout(r, 800));
    } catch(err) {
      console.error('JPEG export error for mode', mode, err);
      alert(`Error exporting ${mode} map: ${err.message}`);
    }
  }
}